import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Alert, Spinner } from 'reactstrap';
import { UploadCloud } from 'react-feather';
import { Breadcrumbs } from '../../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';

const EMPTY = { company_name: '', address: '', email: '', vat_no: '', eori: '', bank_details: '', fiscal_rep: '', reverse_vat_charge: '0', zero_rated_goods: false, season_label: '' };

const CompanySettings = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type, text }
  const [logoVer, setLogoVer] = useState(Date.now()); // cache-buster for the preview
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const inputRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChecked = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/settings/company`);
      if (res.data.settings) {
        const s = res.data.settings;
        setForm({
          company_name: s.company_name || '', address: s.address || '', email: s.email || '',
          vat_no: s.vat_no || '', eori: s.eori || '', bank_details: s.bank_details || '', fiscal_rep: s.fiscal_rep || '',
          reverse_vat_charge: s.reverse_vat_charge != null ? String(s.reverse_vat_charge) : '0',
          zero_rated_goods: !!Number(s.zero_rated_goods),
          season_label: s.season_label || 'SPRING SUMMER 2025',
        });
      }
    } catch (e) {
      setStatus({ type: 'danger', text: 'Failed to load company settings.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await axios.put(`${API_BASE_URL}/api/settings/company`, form);
      setStatus({ type: 'success', text: 'Company settings saved. These now appear on your invoices.' });
    } catch (e) {
      setStatus({ type: 'danger', text: e.response?.data?.error || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    setUploadingLogo(true);
    try {
      await axios.post(`${API_BASE_URL}/api/settings/company/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoVer(Date.now());
      setStatus({ type: 'success', text: 'Logo updated.' });
    } catch (e) {
      setStatus({ type: 'danger', text: e.response?.data?.error || 'Failed to upload logo.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Company" parent="Settings" title="Company" />
      <Container fluid={true}>
        {status && <Alert color={status.type}>{status.text}</Alert>}
        <Row>
          <Col lg="8">
            <Card>
              <CardHeader>
                <h4>Company Details</h4>
                <span>Used on invoices, proforma invoices and order documents.</span>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5"><Spinner size="sm" /> <span className="text-muted ms-1">Loading…</span></div>
                ) : (
                  <Row>
                    <Col md="12" className="mb-3">
                      <Label className="form-label">Company Name</Label>
                      <Input value={form.company_name} onChange={set('company_name')} />
                    </Col>
                    <Col md="12" className="mb-3">
                      <Label className="form-label">Address</Label>
                      <Input type="textarea" rows="3" value={form.address} onChange={set('address')} />
                    </Col>
                    <Col md="12" className="mb-3">
                      <Label className="form-label">Season / Collection Label</Label>
                      <Input value={form.season_label} onChange={set('season_label')} placeholder="SPRING SUMMER 2025" />
                      <small className="text-muted">Shown under the invoice/proforma title, e.g. &ldquo;SPRING SUMMER 2025&rdquo;.</small>
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label className="form-label">Email</Label>
                      <Input value={form.email} onChange={set('email')} />
                    </Col>
                    <Col md="3" className="mb-3">
                      <Label className="form-label">VAT No</Label>
                      <Input value={form.vat_no} onChange={set('vat_no')} />
                    </Col>
                    <Col md="3" className="mb-3">
                      <Label className="form-label">EORI</Label>
                      <Input value={form.eori} onChange={set('eori')} />
                    </Col>
                    <Col md="3" className="mb-3">
                      <Label className="form-label">Reverse VAT Charge (%)</Label>
                      <Input
                        type="number" step="0.01" min="0" max="100"
                        value={form.reverse_vat_charge}
                        onChange={set('reverse_vat_charge')}
                        disabled={form.zero_rated_goods}
                      />
                    </Col>
                    <Col md="12" className="mb-3">
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="zeroRatedGoods"
                          role="switch"
                          checked={form.zero_rated_goods}
                          onChange={setChecked('zero_rated_goods')}
                        />
                        <Label className="form-check-label" for="zeroRatedGoods">Zero-rated goods</Label>
                      </div>
                      <small className="text-muted">
                        When on, invoices, proforma invoices and credit notes show the goods as a
                        zero-rated supply — a <em>&ldquo;Zero-rated goods (0% VAT)&rdquo;</em> line at 0.00.
                        This takes precedence over the Reverse VAT Charge rate above.
                      </small>
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label className="form-label">Bank Details</Label>
                      <Input type="textarea" rows="5" value={form.bank_details} onChange={set('bank_details')} />
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label className="form-label">Fiscal Representative</Label>
                      <Input type="textarea" rows="5" value={form.fiscal_rep} onChange={set('fiscal_rep')} />
                    </Col>
                    <Col md="12">
                      <Button color="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg="4">
            <Card>
              <CardHeader>
                <h4>Company Logo</h4>
                <span>Shown on invoice documents.</span>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-3 p-3" style={{ background: 'var(--rf-card-muted)', borderRadius: 12 }}>
                  <img
                    src={`${API_BASE_URL}/api/settings/company/logo?v=${logoVer}`}
                    alt="Company logo"
                    style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div
                  className="rf-dropzone"
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current && inputRef.current.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current && inputRef.current.click(); } }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); uploadLogo(e.dataTransfer.files[0]); }}
                >
                  <div className="rf-dropzone-icon"><UploadCloud /></div>
                  <h6>{uploadingLogo ? 'Uploading…' : 'Upload a new logo'}</h6>
                  <small>PNG or JPG</small>
                </div>
                <input ref={inputRef} type="file" className="d-none" accept="image/*" onChange={(e) => uploadLogo(e.target.files[0])} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default CompanySettings;
